<template>
  <div>
    <v-app-bar flat color="transparent" class="main-appbar">
      <app-bar-nav-icon />
      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <v-toolbar-title v-else class="headline font-weight-bold">
        Users ({{ listMeta.total }})
      </v-toolbar-title>

      <v-spacer></v-spacer>
      <v-row align="center" style="max-width: 300px">
        <v-text-field
          v-if="!loading"
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search name, email or phone number"
          v-model="filter.search"
          @input="search"
        ></v-text-field>
      </v-row>

      <v-skeleton-loader
        type="actions"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <template v-else>
        <v-btn class="ml-5" icon @click="showSearchField">
          <img src="@/assets/icons/search.svg" alt height="17.7px" />
        </v-btn>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              color="#383d3f"
              class="mr-1"
              icon
              @click="exportList"
              v-on="on"
            >
              <v-icon>{{ icons.export }}</v-icon>
            </v-btn>
          </template>
          <span>Export to Excel</span>
        </v-tooltip>
      </template>
    </v-app-bar>

    <div class="mt-12 mx-4" id="user-list">
      <v-simple-table>
        <thead>
          <tr>
            <th
              width="15%"
              class="text-left pa-0"
              @click="sortColumn"
              data-column="full_name"
            >
              Name
              <v-icon v-if="params.sort_by == `full_name`">{{
                icons.selectedArrow
              }}</v-icon>
            </th>
            <th
              width="5%"
              class="text-left"
              @click="sortColumn"
              data-column="birthdate"
            >
              Age
              <v-icon v-if="params.sort_by == `birthdate`">{{
                icons.selectedArrow
              }}</v-icon>
            </th>
            <th
              width="10%"
              class="text-left"
              @click="sortColumn"
              data-column="email"
            >
              Email
              <v-icon v-if="params.sort_by == `email`">{{
                icons.selectedArrow
              }}</v-icon>
            </th>
            <th
              width="5%"
              class="text-left"
              @click="sortColumn"
              data-column="phone_number"
            >
              Phone number
              <v-icon v-if="params.sort_by == `phone_number`">{{
                icons.selectedArrow
              }}</v-icon>
            </th>
            <th
              width="10%"
              class="text-left"
              data-column="payouts_enabled"
              @click="sortColumn"
            >
              Payout Status
              <v-icon v-if="params.sort_by == `payouts_enabled`">{{
                icons.selectedArrow
              }}</v-icon>
            </th>
            <th
              width="10%"
              class="text-left"
              data-column="charges_enabled"
              @click="sortColumn"
            >
              Charge Status
              <v-icon v-if="params.sort_by == `charges_enabled`">{{
                icons.selectedArrow
              }}</v-icon>
            </th>
            <th
              width="10%"
              class="text-left"
              data-column="stripe_connect_verification_status"
              @click="sortColumn"
            >
              Stripe Account Status
              <v-icon
                v-if="params.sort_by == `stripe_connect_verification_status`"
                >{{ icons.selectedArrow }}</v-icon
              >
            </th>
            <th
              width="15%"
              class="text-left"
              @click="sortColumn"
              data-column="created_at"
            >
              Added date
              <v-icon v-if="params.sort_by == `created_at`">{{
                icons.selectedArrow
              }}</v-icon>
            </th>
            <th width="10%" class="text-left">Blocked</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="user in users"
            :key="user.id"
            v-show="!isLoadingPagination"
          >
            <td
              class="clickable"
              @click="
                $router.push({ name: 'user.details', params: { id: user.id } })
              "
            >
              <v-row no-gutters>
                <v-col md="4" lg="3" sm="12" class="mr-5">
                  <user-avatar :avatar="user.avatar" size="40" />
                </v-col>
                <v-col md="7" lg="7" sm="12" class="pl-2">
                  {{ user.full_name }}
                </v-col>
              </v-row>
            </td>
            <td>{{ user.age }}</td>

            <td>{{ user.email }}</td>
            <td>
              {{ user.phone_number ? '+' + user.phone_number : '' }}
            </td>
            <td>
              <v-badge
                :content="user.stripe_account_styles.payout.message"
                :color="user.stripe_account_styles.payout.style"
                overlap
              >
                <v-icon large> mdi-vuetify </v-icon>
              </v-badge>
            </td>
            <td>
              <v-badge
                :content="user.stripe_account_styles.charge.message"
                :color="user.stripe_account_styles.charge.style"
                overlap
              >
                <v-icon large> mdi-vuetify </v-icon>
              </v-badge>
            </td>
            <td>
              <v-badge
                :content="user.stripe_account_styles.account.message"
                :color="user.stripe_account_styles.account.style"
                overlap
              >
                <v-icon large> mdi-vuetify </v-icon>
              </v-badge>
            </td>
            <td>{{ user.addedDate }}</td>
            <td>
              <v-switch
                v-model="user.blocked_at"
                @change="toggleBlockUser(user, $event)"
              ></v-switch>
            </td>
          </tr>

          <template v-if="loading || isLoadingPagination">
            <tr v-for="index in params.limit" :key="index + '-skeleton'">
              <td class="py-3 pl-0" width="5%">
                <v-skeleton-loader
                  class="avatar-loading--36"
                  type="avatar"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-simple-table>
      <br />
      <v-card
        v-if="initialLoadingPage"
        class="d-flex justify-end mb-6"
        flat
        tile
        no-gutters
      >
        <v-select
          dense
          solo
          class="pagination-limit"
          :items="limits"
          label="Items per page"
          v-model="params.limit"
          @change="onSelectItem"
        ></v-select>

        <v-pagination
          v-model="page"
          :length="listMeta.last_page"
          :total-visible="10"
        ></v-pagination>
      </v-card>
      <v-skeleton-loader
        type="list-item"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>
    </div>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" right>
      {{ snackbar.message }}
      <v-btn color="white" text @click="snackbar.show = false">Ok</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import {
  mdiDotsVertical,
  mdiUnfoldMoreHorizontal,
  mdiMagnify,
  mdiAccountCircleOutline,
  mdiChevronRight,
  mdiFileExcel,
  mdiArrowUpBold,
  mdiArrowDownBold,
} from '@mdi/js'
import ControlsMixin from '@/utils/mixins/Controls'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import UserAvatar from '@/components/UserAvatar'
import infiniteScroll from 'vue-infinite-scroll'
import debounce from 'lodash/debounce'

export default {
  name: 'UsersPage',

  mixins: [ControlsMixin],

  directives: {
    infiniteScroll,
  },

  components: {
    UserAvatar,
    AppBarNavIcon,
  },

  data() {
    return {
      loading: false,
      initialLoadingPage: false,
      isLoadingPagination: false,
      showSearch: false,
      showProfile: false,
      selectedProfile: null,
      filter: {
        search: '',
      },
      icons: {
        settings: mdiDotsVertical,
        sort: mdiUnfoldMoreHorizontal,
        search: mdiMagnify,
        view: mdiAccountCircleOutline,
        arrow: mdiChevronRight,
        export: mdiFileExcel,
        arrowUp: mdiArrowUpBold,
        arrowDown: mdiArrowDownBold,
        selectedArrow: mdiArrowUpBold,
      },
      snackbar: {
        show: false,
        message: null,
        color: null,
      },
      params: {
        sort_direction: 'asc',
        sort_by: null,
        limit: 10,
        role: 'PERSONAL',
      },
      page: 1,
      limits: [5, 10, 20, 30, 50],
    }
  },

  created() {
    this.clearUsers()
    this.fetchUsers(1)
  },

  computed: {
    ...mapState({
      users: (state) => state.user.list,
      listMeta: (state) => state.user.listMeta,
    }),

    paginationLength() {
      return (this.listMeta.total / this.listMeta.per_page) | 0
    },
  },

  methods: {
    ...mapActions({
      getUsers: 'user/getUsers',
      exportToExcel: 'user/exportToExcel',
      blockUser: 'user/blockUser',
    }),

    ...mapMutations({
      clearUsers: 'user/clearUserList',
    }),

    pageChanged(page) {
      this.fetchUsers(page)
    },
    onSelectItem() {
      this.clearUsers()
      this.fetchUsers(1)
    },
    loadMore() {
      if (this.listMeta.current_page < this.listMeta.last_page) {
        this.fetchUsers(this.listMeta.current_page + 1)
      }
    },
    sortColumn(e) {
      this.params.sort_direction =
        this.params.sort_direction === 'asc' ? 'desc' : 'asc'

      this.icons.selectedArrow =
        this.params.sort_direction === 'asc' ? mdiArrowUpBold : mdiArrowDownBold

      this.clearUsers()
      this.params.sort_by = e.target.getAttribute('data-column')
      this.fetchUsers()
    },
    search: debounce(function () {
      this.clearUsers()
      this.fetchUsers()
    }, 600),

    async fetchUsers(page = 1) {
      if (this.loading) return

      let params = this.params
      params.page = page

      if (this.filter.search) {
        params.search = this.filter.search
      } else {
        params.search = ''
      }

      if (!this.initialLoadingPage) {
        this.loading = true
      } else {
        this.isLoadingPagination = true
      }

      await this.getUsers(params)

      if (!this.initialLoadingPage) {
        this.loading = false
      } else {
        this.isLoadingPagination = false
      }

      this.initialLoadingPage = true
    },

    showProfileDialog(profile) {
      this.selectedProfile = profile
      this.showProfile = true
    },

    async exportList() {
      this.loading = true
      await this.exportToExcel()
        .then((response) => {
          const url = URL.createObjectURL(
            new Blob([response.data], {
              type: 'application/vnd.ms-excel',
            })
          )
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'users_list.xlsx')
          document.body.appendChild(link)
          link.click()
          this.showSnackbar('File successfully downloaded', 'success')
        })
        .catch(() => {
          this.showSnackbar('Ops! Something went wrong', 'red')
        })
      this.loading = false
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    },

    async showSearchField() {
      this.showSearch = !this.showSearch
      await this.$nextTick()
      this.$refs.searchField.$refs.input.focus()
    },

    async toggleBlockUser(user, event) {
      const action = user.blocked_at ? 'blocked' : 'unblocked'

      this.blockUser({
        id: user.id,
        action,
      })
        .then(() => {
          this.showSnackbar(`User successfully ${action}!`, 'success')
        })
        .catch((err) => {
          this.showSnackbar(err.response.data.message, 'error')
        })
    },
  },
  watch: {
    page() {
      this.pageChanged(this.page)
    },
  },
}
</script>
